import type { TaskMenuTarget } from '@/src/renderer/features/taskManagement/components/TaskMenu/TaskMenu.types'
import type { TaskMenuMarks } from '@/src/renderer/features/taskManagement/components/TaskMenu/taskMenuMarks'
import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { SquarePen } from 'lucide-react'
import { Button } from '../../../../../../components/ui/Button'
import type { Task } from '../../../../../../lib/db/models'
import type { TaskCreationData } from '../../../../components/TaskEntry'
import type { MonthBucket } from '../../hooks/upcomingTasks.utils'
import { DayGroup } from '../DayGroup'
import { EMPTY_SELECTION } from '../../hooks/useUpcomingSelection'
import { MonthTitle } from '../MonthTitle'
import { DecorativeWave } from '../../../../../../components/ui/DecorativeWave'
import './MonthBlock.scss'

export interface MonthBlockProps {
  bucket: MonthBucket
  /** Register this block's anchor ref under `bucket.monthKey` in the parent map. */
  registerAnchor: (monthKey: string, el: HTMLElement | null) => void
  /** Day-key of today (midnight-refreshed upstream). */
  todayDateKey: string
  /** Page-wide editing-task-id. The block narrows it per day so days without
   *  the edited task hit their React.memo short-circuit. */
  editingTaskId: string | null
  /** Ausgewählte Aufgaben je Tagesschlüssel. Die Seite baut die Karte mit
   *  stabilen Mengen (Tage ohne Auswahl teilen sich EINE leere), damit
   *  `DayGroup` seinen `React.memo` behält. */
  selectedIdsByDay: ReadonlyMap<string, ReadonlySet<string>>
  /** Page-wide day-key currently in "create new task" mode. */
  creatingDateKey: string | null
  onStartEdit: (taskId: string) => void
  onCancelEdit: () => void
  onNavigateEdit: (taskId: string, direction: 'next' | 'prev') => void
  onSelectTask: (taskId: string, e: React.MouseEvent) => void
  onStartCreate: (dateKey: string) => void
  onCancelCreate: () => void
  onSubmitCreate: (dateKey: string, data: TaskCreationData) => void
  onToggleComplete: (taskId: string) => void
  onDelete: (taskId: string) => void
  onUpdateTask: (taskId: string, data: TaskCreationData) => void
  onDuplicate?: (taskId: string) => void
  /** „Im Projekt zeigen“ im Rechtsklick-Menü (#257). */
  onShowInProject?: (taskId: string) => void
  onContextMenuBadgeUpdate?: (taskId: string, updates: Partial<Omit<Task, 'id'>>) => void
  getContextMenuMarks?: (clickedTaskId: string) => TaskMenuMarks
  /**
   * **Play** — beginnt einen **Fokus** an dieser Aufgabe (Kürzel `P`).
   * Fehlt er, tut der Play-Knopf der Zeile nichts.
   */
  onPlay?: (taskId: string) => void
  /** Läuft gerade ein Zug? Schaltet die Übergänge an den Zeilen scharf. */
  isDragActive?: boolean
  menuShortcut?: { taskId: string; target: TaskMenuTarget } | null
  onMenuShortcutClose?: () => void
}

const MonthBlockComponent: React.FC<MonthBlockProps> = ({
  bucket,
  registerAnchor,
  todayDateKey,
  editingTaskId,
  selectedIdsByDay,
  creatingDateKey,
  onStartEdit,
  onCancelEdit,
  onNavigateEdit,
  onSelectTask,
  onStartCreate,
  onCancelCreate,
  onSubmitCreate,
  onToggleComplete,
  onDelete,
  onUpdateTask,
  onDuplicate,
  onShowInProject,
  onContextMenuBadgeUpdate,
  getContextMenuMarks,
  isDragActive,
  menuShortcut, onMenuShortcutClose, onPlay,
}) => {
  const { t } = useTranslation()

  // Register the full <section> element as the anchor so the IO + overlap
  // tracker in useVisibleMonth measures the month's real visible height.
  // Ref callback (not useEffect) guarantees registration lands inside the
  // same commit that renders the block — the parent's initial-scroll
  // useLayoutEffect depends on this.
  const setAnchor = useCallback((el: HTMLElement | null) => {
    registerAnchor(bucket.monthKey, el)
  }, [bucket.monthKey, registerAnchor])

  const renderDay = (day: MonthBucket['days'][number]) => {
    // Narrow the page-wide edit/select IDs down to just this day so
    // days without the active task hit DayGroup's React.memo and skip
    // re-rendering when the user expands something elsewhere.
    const dayEditing = day.tasks.some(t => t.id === editingTaskId) ? editingTaskId : null
    const daySelected = selectedIdsByDay.get(day.dateKey) ?? EMPTY_SELECTION
    const dayIsCreating = creatingDateKey === day.dateKey
    return (
      <DayGroup
        key={day.dateKey}
        date={day.date}
        tasks={day.tasks}
        todayDateKey={todayDateKey}
        editingTaskId={dayEditing}
        selectedIds={daySelected}
        isCreating={dayIsCreating}
        onStartEdit={onStartEdit}
        onCancelEdit={onCancelEdit}
        onNavigateEdit={onNavigateEdit}
        onSelectTask={onSelectTask}
        onStartCreate={onStartCreate}
        onCancelCreate={onCancelCreate}
        onSubmitCreate={onSubmitCreate}
        onToggleComplete={onToggleComplete}
        onDelete={onDelete}
        onUpdateTask={onUpdateTask}
        onDuplicate={onDuplicate}
        onShowInProject={onShowInProject}
        onContextMenuBadgeUpdate={onContextMenuBadgeUpdate}
        getContextMenuMarks={getContextMenuMarks}
        isDragActive={isDragActive}
        menuShortcut={menuShortcut}
        onPlay={onPlay}
        onMenuShortcutClose={onMenuShortcutClose}
      />
    )
  }

  // Leerer Monat: Statt „Keine Termine“ ein Knopf, der hier eine Aufgabe anlegt
  // (24.09.2026). Im laufenden Monat ab heute, sonst am Ersten.
  const createDateKey = (bucket.days.find(d => d.dateKey >= todayDateKey) ?? bucket.days[0]).dateKey
  const creatingDay = bucket.hasAnyTasks ? undefined : bucket.days.find(d => d.dateKey === creatingDateKey)

  return (
    <section
      ref={setAnchor}
      className="month-block"
      data-month-key={bucket.monthKey}
      data-month-anchor={bucket.monthKey}
    >
      {/* Inline header — replicates the top title so the user always sees
          which month they're browsing. The pinned top header tracks whichever
          month owns the most viewport pixels, so this inline title feels like
          it hands the baton up when scrolling past. */}
      <div className="month-block__title">
        <MonthTitle month={bucket.firstDay} size="sm" showYear={false} />
        <DecorativeWave className="month-block__title-wave" />
      </div>

      {bucket.hasAnyTasks ? (
        bucket.days.map(renderDay)
      ) : creatingDay ? (
        renderDay(creatingDay)
      ) : (
        <div className="month-block__empty">
          <Button
            variant="outline"
            icon={<SquarePen size={16} />}
            onClick={() => onStartCreate(createDateKey)}
          >
            {t('upcoming.emptyMonth.createTask')}
          </Button>
        </div>
      )}
    </section>
  )
}

export const MonthBlock = React.memo(MonthBlockComponent)
